import { useState, useMemo } from 'react'
import { useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Briefcase, Search, SlidersHorizontal } from 'lucide-react'
import { api } from '@/lib/api'
import {
  CareerPageLayout,
  CareerJobCard,
  CareerSearchFilter,
} from '@/components/features/career/CareerPageLayout'

interface Job {
  id: string
  slug: string
  title: string
  department?: { name: string }
  location?: { name: string }
  employmentType: string
  remotePolicy: string
  createdAt: string
}

interface PublicJobsResponse {
  company: {
    name: string
    logo: string | null
  }
  jobs: Job[]
}

export function CareerPage() {
  const { tenant: tenantSlug } = useParams({ strict: false }) as { tenant: string }
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDepartment, setSelectedDepartment] = useState('')
  const [selectedLocation, setSelectedLocation] = useState('')

  // Fetch public jobs (includes company info)
  const { data: jobsData, isLoading: jobsLoading } = useQuery({
    queryKey: ['public-jobs', tenantSlug],
    queryFn: () => api.get<PublicJobsResponse>(`/public/${tenantSlug}/jobs`),
  })

  const jobs = (jobsData as PublicJobsResponse)?.jobs || []
  const companyInfo = (jobsData as PublicJobsResponse)?.company

  // Extract unique departments and locations
  const departments = useMemo(() => {
    const depts = new Set<string>()
    jobs.forEach((job: Job) => {
      if (job.department?.name) depts.add(job.department.name)
    })
    return Array.from(depts).sort()
  }, [jobs])

  const locations = useMemo(() => {
    const locs = new Set<string>()
    jobs.forEach((job: Job) => {
      if (job.location?.name) locs.add(job.location.name)
    })
    return Array.from(locs).sort()
  }, [jobs])

  // Filter jobs
  const filteredJobs = useMemo(() => {
    return jobs.filter((job: Job) => {
      const matchesSearch = !searchQuery ||
        job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.department?.name?.toLowerCase().includes(searchQuery.toLowerCase())

      const matchesDepartment = !selectedDepartment ||
        job.department?.name === selectedDepartment

      const matchesLocation = !selectedLocation ||
        job.location?.name === selectedLocation

      return matchesSearch && matchesDepartment && matchesLocation
    })
  }, [jobs, searchQuery, selectedDepartment, selectedLocation])

  // Group jobs by department
  const groupedJobs = useMemo(() => {
    const grouped: Record<string, Job[]> = {}
    filteredJobs.forEach((job: Job) => {
      const dept = job.department?.name || 'General'
      if (!grouped[dept]) grouped[dept] = []
      grouped[dept].push(job)
    })
    return grouped
  }, [filteredJobs])

  if (jobsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    )
  }

  const pageSettings = {
    companyName: companyInfo?.name || tenantSlug,
    companyLogo: companyInfo?.logo || undefined,
  }

  return (
    <CareerPageLayout settings={pageSettings} tenantSlug={tenantSlug}>
      {/* Search and Filters */}
      <CareerSearchFilter
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        departments={departments}
        selectedDepartment={selectedDepartment}
        onDepartmentChange={setSelectedDepartment}
        locations={locations}
        selectedLocation={selectedLocation}
        onLocationChange={setSelectedLocation}
      />

      {/* Results Count */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-muted-foreground">
          {filteredJobs.length} {filteredJobs.length === 1 ? 'position' : 'positions'} available
        </p>
        {(selectedDepartment || selectedLocation || searchQuery) && (
          <button
            onClick={() => {
              setSearchQuery('')
              setSelectedDepartment('')
              setSelectedLocation('')
            }}
            className="text-sm text-primary hover:underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Job Listings */}
      {jobsLoading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-24 skeleton rounded-xl" />
          ))}
        </div>
      ) : filteredJobs.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-16"
        >
          <Briefcase className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium mb-2">No positions found</h3>
          <p className="text-muted-foreground">
            {searchQuery || selectedDepartment || selectedLocation
              ? 'Try adjusting your search or filters'
              : 'Check back later for new opportunities'}
          </p>
        </motion.div>
      ) : (
        <div className="space-y-8">
          {Object.entries(groupedJobs).map(([department, deptJobs]) => (
            <div key={department}>
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-primary" />
                {department}
              </h2>
              <div className="grid gap-4">
                {deptJobs.map((job: Job, index: number) => (
                  <motion.div
                    key={job.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <CareerJobCard
                      job={{
                        id: job.id,
                        slug: job.slug,
                        title: job.title,
                        department: job.department?.name,
                        location: job.location?.name,
                        employmentType: job.employmentType,
                        remotePolicy: job.remotePolicy,
                        postedAt: job.createdAt,
                      }}
                      tenantSlug={tenantSlug}
                    />
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Job Alerts CTA */}
      {filteredJobs.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12 p-8 bg-muted/50 rounded-2xl text-center"
        >
          <h3 className="text-xl font-semibold mb-2">Don't see the right fit?</h3>
          <p className="text-muted-foreground mb-4">
            Sign up to get notified when new positions open up
          </p>
          <div className="flex max-w-md mx-auto gap-2">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 h-11 px-4 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
            <button className="h-11 px-6 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors">
              Subscribe
            </button>
          </div>
        </motion.div>
      )}
    </CareerPageLayout>
  )
}
